/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { getQueryClient } from '@opengeoweb/api';
import type { Credentials } from '@opengeoweb/authentication';
import { viewpresetQueryOptions } from '../api/viewpreset-api/queryOptions';
import type { ViewPreset } from '../store/viewPresets/types';
import type {
  WorkspacePresetForBE,
  WorkspaceViewsFromBE,
} from '../store/workspace/types';

export const writeToClipboard = async (text: string): Promise<void> => {
  window.focus();
  await navigator.clipboard.writeText(text);
};

/**
 * For each view that only has a viewPresetId reference (old-style), fetch
 * the full view preset from BE and replaces the reference with inline
 * componentType + initialProps.
 */
export const expandWorkspaceViewPresets = async (
  preset: WorkspacePresetForBE,
  auth: Credentials,
): Promise<WorkspacePresetForBE> => {
  const expandedViews = await Promise.all(
    preset.views.map(async (view) => {
      if (!view.viewPresetId || view.componentType) {
        return view;
      }
      const fullPreset = await getQueryClient().fetchQuery(
        viewpresetQueryOptions.getViewPreset(view.viewPresetId, auth),
      );
      return {
        mosaicNodeId: view.mosaicNodeId,
        title: view.title ?? fullPreset.title,
        componentType: fullPreset.componentType,
        initialProps: fullPreset.initialProps,
      };
    }),
  );
  return { ...preset, views: expandedViews };
};

export const sanitizeWorkspaceForClipboard = (
  preset: WorkspacePresetForBE,
): WorkspacePresetForBE => {
  const views = Array.isArray(preset.views)
    ? preset.views.map(
        ({
          originalViewPresetId: _orig,
          ...view
        }: WorkspaceViewsFromBE & { originalViewPresetId?: string }) => view,
      )
    : preset.views;

  return {
    ...preset,
    id: undefined,
    views,
    is_shared: false,
    scope: 'user' as const,
  };
};

export const sanitizeViewPresetForClipboard = (
  preset: ViewPreset,
): ViewPreset => {
  return {
    ...preset,
    keywords: preset.keywords ?? '',
    id: undefined,
    is_shared: false,
    scope: 'user' as const,
  };
};

export const validateWorkspacePresetJson = (
  jsonString: string,
): { error: string; errorParam?: string } | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    return {
      error: 'workspace-paste-validation-invalid-json',
      errorParam: e instanceof Error ? e.message : String(e),
    };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { error: 'workspace-paste-validation-not-object' };
  }

  const obj = parsed as Record<string, unknown>;

  if (typeof obj['componentType'] === 'string') {
    return { error: 'workspace-paste-validation-wrong-preset-type' };
  }

  if (typeof obj['title'] !== 'string' || !obj['title'].trim()) {
    return {
      error: 'workspace-paste-validation-missing-field',
      errorParam: 'title',
    };
  }

  if (!Array.isArray(obj['views'])) {
    return {
      error: 'workspace-paste-validation-missing-field',
      errorParam: 'views',
    };
  }

  if (obj['mosaicNode'] === undefined || obj['mosaicNode'] === null) {
    return {
      error: 'workspace-paste-validation-missing-field',
      errorParam: 'mosaicNode',
    };
  }

  const hasUnexpandedViews = (obj['views'] as Record<string, unknown>[]).some(
    (view) => 'viewPresetId' in view && !('componentType' in view),
  );
  if (hasUnexpandedViews) {
    return { error: 'workspace-paste-validation-unexpanded-views' };
  }

  return null;
};

/**
 * Validates that the parsed value is a valid ViewPreset structure.
 */
export const validateViewPresetJson = (
  jsonString: string,
): { error: string; errorParam?: string } | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    return {
      error: 'workspace-paste-validation-invalid-json',
      errorParam: e instanceof Error ? e.message : String(e),
    };
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { error: 'workspace-paste-validation-not-object' };
  }

  const obj = parsed as Record<string, unknown>;

  if (Array.isArray(obj['views'])) {
    return { error: 'viewpreset-paste-validation-wrong-preset-type' };
  }

  if (typeof obj['title'] !== 'string' || !obj['title'].trim()) {
    return {
      error: 'workspace-paste-validation-missing-field',
      errorParam: 'title',
    };
  }

  if (typeof obj['componentType'] !== 'string' || !obj['componentType']) {
    return {
      error: 'workspace-paste-validation-missing-field',
      errorParam: 'componentType',
    };
  }

  if (typeof obj['keywords'] !== 'string') {
    return {
      error: 'workspace-paste-validation-missing-field',
      errorParam: 'keywords',
    };
  }

  return null;
};
